import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

export default class extends Controller {
  static targets = [
    "dropzone",
    "fileInput",
    "progressContainer",
    "progressFilename",
    "progressPercent",
    "progressBar",
    "errorContainer",
    "errorMessage",
  ]
  static values = {
    friendGroupId: Number,
    seasonId: Number,
    // Where the re-render lands. The drawer and the season page own the
    // defaults; season setup draws this attachment inside its own step and
    // passes its own frame, so the upload doesn't try to swap a frame that
    // isn't on the page.
    frame: { type: String, default: "hero" },
    turboFrame: { type: String, default: "season_hero" },
    maxBytes: { type: Number, default: 4 * 1024 * 1024 },
  }

  connect() {
    this.state = "idle"
    this.lastFile = null
    this.currentXHR = null
    this.cancelled = false
    this.boundDragOver = this.onDragOver.bind(this)
    this.boundDragLeave = this.onDragLeave.bind(this)
    this.boundDrop = this.onDrop.bind(this)

    if (this.hasDropzoneTarget) this.attachDragHandlers(this.dropzoneTarget)
  }

  disconnect() {
    if (this.hasDropzoneTarget) this.detachDragHandlers(this.dropzoneTarget)
    this.abortUpload()
  }

  attachDragHandlers(el) {
    el.addEventListener("dragover", this.boundDragOver)
    el.addEventListener("dragleave", this.boundDragLeave)
    el.addEventListener("drop", this.boundDrop)
  }

  detachDragHandlers(el) {
    el.removeEventListener("dragover", this.boundDragOver)
    el.removeEventListener("dragleave", this.boundDragLeave)
    el.removeEventListener("drop", this.boundDrop)
  }

  onDragOver(event) {
    if (this.state !== "idle") return
    event.preventDefault()
    event.currentTarget.classList.add(
      "border-vivid-purple",
      "bg-vivid-purple/5",
    )
  }

  onDragLeave(event) {
    event.currentTarget.classList.remove(
      "border-vivid-purple",
      "bg-vivid-purple/5",
    )
  }

  onDrop(event) {
    event.preventDefault()
    event.currentTarget.classList.remove(
      "border-vivid-purple",
      "bg-vivid-purple/5",
    )
    if (this.state !== "idle") return
    const file = event.dataTransfer.files[0]
    if (file) this.startUpload(file)
  }

  openPicker(event) {
    event?.preventDefault?.()
    // Only an upload in flight blocks the picker. Refusing in the error state
    // too left the button dead after any failure: Retry re-sends the file that
    // just failed, so without this there was no way to choose a different one.
    if (this.state === "uploading") return
    this.fileInputTarget.click()
  }

  fileSelected(event) {
    const file = event.target.files[0]
    if (file) this.startUpload(file)
    event.target.value = ""
  }

  retry() {
    if (this.lastFile) this.startUpload(this.lastFile)
  }

  cancel() {
    this.cancelled = true
    this.abortUpload()
    this.transitionTo("idle")
  }

  startUpload(file) {
    this.lastFile = file
    const validation = this.validate(file)
    if (validation) {
      this.showError(validation)
      return
    }

    this.cancelled = false
    this.transitionTo("uploading", file)

    const url = "/rails/active_storage/direct_uploads"
    const upload = new DirectUpload(file, url, this)

    upload.create((error, blob) => {
      this.currentXHR = null
      if (error) {
        if (this.cancelled) {
          this.cancelled = false
          return
        }
        const message = error instanceof Error ? error.message : String(error)
        this.showError(`Upload failed: ${message}`)
        return
      }
      this.submitSignedId(blob.signed_id)
    })
  }

  directUploadWillStoreFileWithXHR(xhr) {
    this.currentXHR = xhr
    xhr.upload.addEventListener("progress", (event) => {
      if (!event.lengthComputable) return
      const pct = Math.round((event.loaded / event.total) * 100)
      this.updateProgress(pct)
    })
  }

  abortUpload() {
    if (this.currentXHR) {
      try {
        this.currentXHR.abort()
      } catch (_e) {}
      this.currentXHR = null
    }
  }

  validate(file) {
    const name = (file.name || "").toLowerCase()
    const okExt =
      name.endsWith(".jpg") ||
      name.endsWith(".jpeg") ||
      name.endsWith(".png") ||
      name.endsWith(".webp")
    if (!okExt) return "Only JPG, PNG, or WebP images are allowed."
    if (file.size > this.maxBytesValue) {
      return `File is too large (max ${Math.round(this.maxBytesValue / 1024 / 1024)} MB).`
    }
    return null
  }

  submitSignedId(signedId) {
    const form = document.createElement("form")
    form.method = "post"
    form.action = `/friend_groups/${this.friendGroupIdValue}/seasons/${this.seasonIdValue}`
    form.style.display = "none"
    form.dataset.turboFrame = this.turboFrameValue

    form.appendChild(this.hiddenInput("_method", "patch"))
    form.appendChild(this.hiddenInput("authenticity_token", this.csrfToken()))
    form.appendChild(this.hiddenInput("season[key_visual]", signedId))
    form.appendChild(this.hiddenInput("frame", this.frameValue))

    document.body.appendChild(form)
    form.requestSubmit()
    setTimeout(() => form.remove(), 0)
  }

  hiddenInput(name, value) {
    const i = document.createElement("input")
    i.type = "hidden"
    i.name = name
    i.value = value
    return i
  }

  csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }

  transitionTo(state, file) {
    this.state = state
    if (this.hasDropzoneTarget)
      this.dropzoneTarget.classList.toggle("hidden", state !== "idle")
    if (this.hasFileInputTarget)
      this.fileInputTarget.disabled = state === "uploading"
    if (this.hasProgressContainerTarget) {
      this.progressContainerTarget.classList.toggle(
        "hidden",
        state !== "uploading",
      )
    }
    if (this.hasErrorContainerTarget) {
      this.errorContainerTarget.classList.toggle("hidden", state !== "error")
    }

    if (state === "uploading" && file && this.hasProgressFilenameTarget) {
      this.progressFilenameTarget.textContent = file.name
      this.updateProgress(0)
    }
  }

  updateProgress(pct) {
    if (this.hasProgressPercentTarget)
      this.progressPercentTarget.textContent = `${pct}%`
    if (this.hasProgressBarTarget)
      this.progressBarTarget.style.width = `${pct}%`
  }

  showError(message) {
    if (this.hasErrorMessageTarget)
      this.errorMessageTarget.textContent = message
    this.transitionTo("error")
  }
}
